import { cn } from "@nexxt/ui";
import { useCallback, useRef } from "react";
import { m } from "../../paraglide/messages.js";
import { vars } from "../css-vars";
import { useStill } from "../motion/motion";
import { phaseOf, segment } from "../motion/progress";
import { useScrollProgress } from "../motion/use-scroll-progress";
import { AppMark } from "../scenes/app-window";
import { ChapterKicker, ChapterPoints } from "./chapter";

// Backstage gets its superpowers (ADR-286): while the section scrolls, the CRM comes into Backstage's window and its
// indigo turns amethyst (`--m1`), then the lights go down to Guestlist's night (`--m2`) and the invitations fly out.
// The colours are mixed in styles.css (.morph-window); the phases swap what cannot be mixed: the mark, the places and
// the headline.
const backstagePlaces = ["Home", "Linesheets", "Purchase orders", "Sales orders", "Directory"];
const guestlistPlaces = ["Today", "Accounts", "Brands", "Campaigns", "Email", "Activities"];
const accounts = [
  { name: "Boutique Lumière", city: "Paris", brands: ["Arvo", "Selva"], health: "good" },
  { name: "Nord Concept", city: "Copenhagen", brands: ["Selva"], health: "fading" },
  { name: "Casa Ottanta", city: "Milan", brands: ["Arvo", "Lumen"], health: "good" },
  { name: "Hayashi & Co", city: "Tokyo", brands: ["Lumen"], health: "new" },
  { name: "The Field Store", city: "London", brands: ["Arvo"], health: "good" },
];
const funnel = [
  { label: () => m.scene_guestlist_invited(), value: 420 },
  { label: () => m.scene_guestlist_booked(), value: 180 },
  { label: () => m.scene_guestlist_ordered(), value: 96 },
  { label: () => m.scene_guestlist_approved(), value: 88 },
];
const timeline = [
  m.scene_guestlist_timeline_invited,
  m.scene_guestlist_timeline_opened,
  m.scene_guestlist_timeline_booked,
  m.scene_guestlist_timeline_ordered,
];

export function GuestlistMorph() {
  const still = useStill();
  const trackRef = useRef<HTMLDivElement>(null);
  const write = useCallback((track: HTMLElement, p: number) => {
    track.style.setProperty("--m1", String(segment(p, 0.06, 0.42)));
    track.style.setProperty("--m2", String(segment(p, 0.42, 0.6)));
    const phase = phaseOf(p, [0.24, 0.51, 0.62]);
    track.dataset.phase = String(phase);
    // the night under the top bar: the bar and the rail go dark with it
    const { top, bottom } = track.getBoundingClientRect();
    document.documentElement.toggleAttribute("data-night", phase >= 2 && top < 64 && bottom > 64);
  }, []);
  useScrollProgress(trackRef, write, { still, stillAt: 1 });

  return (
    <section id="guestlist" className="morph" data-app="guestlist" aria-labelledby="guestlist-title">
      <div ref={trackRef} className="morph-track" data-still={still || undefined}>
        <div className="morph-stage">
          <div className="morph-copy">
            <ChapterKicker number={2} label="Guestlist" coming />
            <p className="chapter-for">{m.guestlist_audience()}</p>
            <h2 id="guestlist-title" className="morph-headline">
              <span data-headline="before">{m.guestlist_headline_before()}</span>
              <span data-headline="after">{m.guestlist_headline_after()}</span>
            </h2>
            <div className="morph-more">
              <p className="lead">{m.guestlist_body()}</p>
              <ChapterPoints points={guestlistPoints()} />
            </div>
          </div>
          <div className="morph-window-wrap">
            <div aria-hidden className="app-window morph-window">
              <div className="app-window-chrome">
                <i />
                <i />
                <i />
                <span className="morph-swap">
                  <span data-was>backstage.nexxt</span>
                  <span data-now>guestlist.nexxt</span>
                </span>
              </div>
              <div className="app-window-bar morph-swap">
                <span data-was>
                  <AppMark app="backstage" />
                </span>
                <span data-now>
                  <AppMark app="guestlist" />
                </span>
              </div>
              <div className="app-window-body">
                <div className="app-window-places morph-swap">
                  <ul data-was>
                    {backstagePlaces.map((place) => (
                      <li key={place} className={cn(place === "Directory" && "is-current")}>
                        {place}
                      </li>
                    ))}
                  </ul>
                  <ul data-now>
                    {guestlistPlaces.map((place) => (
                      <li key={place} className={cn(place === "Accounts" && "is-current")}>
                        {place}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="app-window-main crm">
                  <div className="crm-list">
                    {accounts.map((account, i) => (
                      <div key={account.name} className="crm-row" style={vars({ "--i": i })}>
                        <span className={cn("crm-health", `is-${account.health}`)} />
                        <span className="crm-name">
                          <strong>{account.name}</strong>
                          <span>{account.city}</span>
                        </span>
                        <span className="crm-brands">
                          {account.brands.map((brand) => (
                            <i key={brand} className="crm-brand">
                              {brand}
                            </i>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="crm-panel">
                    <p className="crm-panel-kicker">{m.scene_guestlist_account()}</p>
                    <p className="crm-panel-title">Boutique Lumière</p>
                    <ol className="crm-timeline">
                      {timeline.map((text, i) => (
                        <li key={text()} className="crm-event" style={vars({ "--i": i })}>
                          {text()}
                        </li>
                      ))}
                    </ol>
                    <p className="crm-panel-kicker">{m.scene_guestlist_funnel()}</p>
                    <div className="crm-funnel">
                      {funnel.map((bar) => (
                        <div key={bar.value} className="crm-bar" style={vars({ "--w": bar.value / 420 })}>
                          <span>{bar.label()}</span>
                          <b className="tabular-nums">{bar.value}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div aria-hidden className="invitations">
              {[0, 1, 2, 3, 4].map((i) => (
                <i key={i} className="invitation" style={vars({ "--i": i })} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="morph-more-narrow">
        <p className="lead">{m.guestlist_body()}</p>
        <ChapterPoints points={guestlistPoints()} />
      </div>
    </section>
  );
}

const guestlistPoints = () => [
  m.guestlist_point_1(),
  m.guestlist_point_2(),
  m.guestlist_point_3(),
  m.guestlist_point_4(),
];
